import { useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { Lock } from '@mui/icons-material'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import type { NdaVersionDto } from '@api/types/nda'
import { shortHash } from './ndaText'

interface NdaVersionHistoryProps {
  versions: NdaVersionDto[]
  isLoading: boolean
  error: unknown
  /** Loads a past version's text into the draft. Publishing it would still mint a new version. */
  onUseAsDraft: (version: NdaVersionDto) => void
  /** Narrows the acceptance record below to one version. */
  onShowAcceptances: (version: number) => void
}

/**
 * The version history, presented as a sealed record rather than a list of documents to edit.
 *
 * Nothing in this table is editable, and the only way to act on a past version is to copy its
 * text into a draft — which publishes a new version rather than restoring the old one.
 */
export function NdaVersionHistory({
  versions,
  isLoading,
  error,
  onUseAsDraft,
  onShowAcceptances,
}: NdaVersionHistoryProps) {
  const [viewing, setViewing] = useState<NdaVersionDto | null>(null)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const mobileDialogProps = useMobileDialogProps()

  const columns = useMemo<DataTableColumn<NdaVersionDto>[]>(
    () => [
      {
        key: 'version',
        header: 'Version',
        primary: true,
        render: (version) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={600}>
              {version.version}
            </Typography>
            {version.isCurrent ? (
              <Chip size="small" color="primary" label="In force" />
            ) : (
              <Tooltip title="Superseded. Still on the record with its signatures.">
                <Chip size="small" variant="outlined" icon={<Lock sx={{ fontSize: 14 }} />} label="Sealed" />
              </Tooltip>
            )}
          </Stack>
        ),
      },
      {
        key: 'title',
        header: 'Title',
        render: (version) => version.title || <em>Untitled</em>,
      },
      {
        key: 'published',
        header: 'Published',
        hideBelow: 'md',
        render: (version) => (
          <>
            <Typography variant="body2">{new Date(version.createdAt).toLocaleString()}</Typography>
            <Tooltip title={`Published by user ${version.createdByUserId}`}>
              <Typography variant="caption" color="text.secondary">
                by {version.createdByUserId?.slice(0, 8) || 'unknown'}…
              </Typography>
            </Tooltip>
          </>
        ),
      },
      {
        key: 'hash',
        header: 'Text',
        hideBelow: 'lg',
        render: (version) => (
          <Tooltip title={version.contentHash}>
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
              {shortHash(version.contentHash)}
            </Typography>
          </Tooltip>
        ),
      },
      {
        key: 'accepted',
        header: 'Accepted by',
        align: 'right',
        render: (version) => (
          <Button size="small" onClick={() => onShowAcceptances(version.version)}>
            {version.acceptedCount}
          </Button>
        ),
      },
    ],
    [onShowAcceptances]
  )

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h6" gutterBottom>
        Version history
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every version ever published, newest first. Versions are immutable: none of these can be
        edited or withdrawn, because each one is the exact text somebody agreed to.
      </Typography>

      {/* The "nothing has ever been published" case is a statement about the gate, not an empty
          list, so it keeps its own wording rather than DataTable's empty row. */}
      {!isLoading && !error && versions.length === 0 ? (
        <Alert severity="info">
          No version has ever been published, so there is nothing to accept and the app is ungated.
        </Alert>
      ) : (
        <DataTable
          rows={versions}
          columns={columns}
          getRowKey={(version) => String(version.id)}
          loading={isLoading}
          error={error ? `Failed to load the version history. ${(error as Error)?.message ?? ''}` : null}
          dense
          // No `total` => DataTable pages the rows it was given.
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[10, 25, 50]}
          rowActions={(version) => (
            <Button size="small" onClick={() => setViewing(version)}>
              View text
            </Button>
          )}
        />
      )}

      <Dialog
        open={!!viewing}
        onClose={() => setViewing(null)}
        maxWidth="md"
        fullWidth
        {...mobileDialogProps}
      >
        <DialogTitle>
          Version {viewing?.version} — {viewing?.title || 'Untitled'}
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" icon={<Lock />} sx={{ mb: 2 }}>
            Read-only. Published text can never be changed — {viewing?.acceptedCount ?? 0}{' '}
            {viewing?.acceptedCount === 1 ? 'person has' : 'people have'} agreed to these exact
            words. To change the agreement, start a draft and publish the next version.
          </Alert>
          <Typography
            component="pre"
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', m: 0 }}
          >
            {viewing?.body}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (viewing) onUseAsDraft(viewing)
              setViewing(null)
            }}
          >
            Start a draft from this text
          </Button>
          <Button variant="contained" onClick={() => setViewing(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}
